import { motion } from "framer-motion";
import { Zap, Users, Globe, Target, Code2, CreditCard, Receipt, Sparkles } from "lucide-react";
import SEO from "../components/SEO";

export default function About() {
    return (
        <div className="min-h-screen bg-base transition-colors duration-300">
            <SEO
                title="About Us"
                description="Learn about the mission behind blinqbooks. We're building the first truly integrated AI business platform for the next generation of entrepreneurs."
            />
            {/* Hero Section */}
            <section className="relative py-20 overflow-hidden">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/10 dark:bg-blue-600/20 rounded-full blur-[100px] -z-10"></div>
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600/10 dark:bg-purple-600/20 rounded-full blur-[100px] -z-10"></div>

                {/* Floating Sparkles */}
                <div className="absolute inset-0 pointer-events-none">
                    {[...Array(6)].map((_, i) => (
                        <motion.div
                            key={i}
                            className="absolute bg-brand-primary rounded-full opacity-10 dark:opacity-30"
                            style={{
                                width: Math.random() * 4 + 2 + "px",
                                height: Math.random() * 4 + 2 + "px",
                                top: Math.random() * 100 + "%",
                                left: Math.random() * 100 + "%",
                            }}
                            animate={{
                                y: [0, -40, 0],
                                scale: [0, 1.5, 0],
                                opacity: [0, 0.4, 0],
                            }}
                            transition={{
                                duration: Math.random() * 2 + 3,
                                repeat: Infinity,
                                ease: "easeInOut",
                                delay: Math.random() * 5,
                            }}
                        />
                    ))}
                    <motion.div
                        className="absolute top-1/4 right-1/3 text-blue-400/20"
                        animate={{ rotate: 360, scale: [1, 1.2, 1] }}
                        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                    >
                        <Sparkles size={120} />
                    </motion.div>
                </div>

                <div className="max-w-4xl mx-auto text-center px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <span className="inline-flex items-center gap-2 py-1 px-3 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 text-sm font-semibold mb-6">
                            <Sparkles className="w-4 h-4" />
                            The blinqbooks Mission
                        </span>
                        <h1 className="text-5xl md:text-7xl font-bold text-primary mb-6 leading-tight">
                            Innovating at the <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400">
                                Speed of AI
                            </span>
                        </h1>
                        <p className="text-xl text-secondary max-w-2xl mx-auto leading-relaxed">
                            We're a team of engineers and dreamers building the All-In-One Platform made for modern accounting. No fragmentation. No legacy code. Just pure scalability.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* The Story / Origin */}
            <section className="py-20 bg-surface transition-colors duration-300">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="bg-slate-500/[0.02] dark:bg-white/[0.01] p-8 rounded-2xl"
                        >
                            <Code2 className="w-12 h-12 text-blue-600 dark:text-blue-500 mb-6" />
                            <h2 className="text-3xl font-bold text-primary mb-4">Born from Frustration</h2>
                            <p className="text-secondary leading-relaxed mb-4">
                                It started with a simple observation: entrepreneurs were drowning in subscriptions. One app for invoices, another for expenses, a third for their website.
                            </p>
                            <p className="text-secondary leading-relaxed">
                                We asked, "What if one intelligent system could do it all?" So we gathered a team of top-tier developers and built blinqbooks—not as a collection of tools, but as a unified core for your entire enterprise.
                            </p>
                        </motion.div>
                        <div className="relative h-full min-h-[300px] rounded-2xl overflow-hidden bg-gradient-to-br from-blue-100/50 to-purple-100/50 dark:from-blue-900/20 dark:to-purple-900/20 flex items-center justify-center">
                            {/* Abstract 'Garage' Tech Visual */}
                            <div className="relative w-full h-full flex items-center justify-center">
                                {/* Chaos Elements (Floating Apps) */}
                                {[
                                    { icon: <CreditCard className="w-8 h-8 text-blue-500 dark:text-blue-400" />, delay: 0, x: -80, y: -40 },
                                    { icon: <Users className="w-8 h-8 text-purple-500 dark:text-purple-400" />, delay: 0.2, x: 80, y: -60 },
                                    { icon: <Globe className="w-8 h-8 text-green-500 dark:text-green-400" />, delay: 0.4, x: -60, y: 60 },
                                    { icon: <Receipt className="w-8 h-8 text-pink-500 dark:text-pink-400" />, delay: 0.6, x: 60, y: 40 },
                                ].map((item, i) => (
                                    <motion.div
                                        key={i}
                                        className="absolute p-3 rounded-xl bg-white/80 dark:bg-white/5 border border-white/20 dark:border-white/10 backdrop-blur-sm shadow-md dark:shadow-none"
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{
                                            opacity: [0, 1, 0.5],
                                            scale: [0, 1, 0.8],
                                            x: [item.x * 1.5, item.x, 0],
                                            y: [item.y * 1.5, item.y, 0],
                                        }}
                                        transition={{
                                            duration: 3,
                                            repeat: Infinity,
                                            repeatType: "reverse",
                                            ease: "easeInOut",
                                            delay: item.delay
                                        }}
                                    >
                                        {item.icon}
                                    </motion.div>
                                ))}

                                {/* Central Core (Unity) */}
                                <motion.div
                                    className="relative z-10 p-6 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 shadow-xl shadow-blue-500/30"
                                    animate={{ scale: [0.9, 1.1, 0.9] }}
                                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                                >
                                    <Zap className="w-10 h-10 text-white fill-white" />
                                </motion.div>

                                {/* Connecting Lines */}
                                <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-50 dark:opacity-30">
                                    <motion.circle cx="50%" cy="50%" r="60" stroke="url(#gradient)" strokeWidth="1.5" fill="none"
                                        animate={{ scale: [1, 1.5], opacity: [0.5, 0] }}
                                        transition={{ duration: 2, repeat: Infinity }}
                                    />
                                    <defs>
                                        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#3b82f6" />
                                            <stop offset="100%" stopColor="#a855f7" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="py-24 bg-base transition-colors duration-300">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <h2 className="text-3xl font-bold text-primary mb-16">Our Startup DNA</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { icon: <Target className="w-8 h-8" />, title: "Obsession", desc: "Customer growth is our only metric that matters." },
                            { icon: <Zap className="w-8 h-8" />, title: "Velocity", desc: "We ship fast. We learn faster. We iterate daily." },
                            { icon: <Globe className="w-8 h-8" />, title: "Impact", desc: "Building software that empowers millions of SMEs." }
                        ].map((val, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="card card-hover"
                            >
                                <div className="w-16 h-16 mx-auto bg-blue-500/10 rounded-xl flex items-center justify-center text-blue-600 dark:text-blue-400 mb-6">
                                    {val.icon}
                                </div>
                                <h3 className="text-xl font-bold text-primary mb-3">{val.title}</h3>
                                <p className="text-secondary">{val.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Team Section */}
            <section className="py-32 bg-surface relative overflow-hidden transition-colors duration-300">
                {/* Background Decorators for Light/Dark harmony */}
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/10 dark:bg-blue-600/10 rounded-full blur-[120px] pointer-events-none -mt-32 -mr-32" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-500/10 dark:bg-purple-600/10 rounded-full blur-[120px] pointer-events-none -mb-32 -ml-32" />

                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center"
                    >
                        <span className="inline-block py-1.5 px-4 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-bold uppercase mb-4 tracking-wider">
                            Our Team
                        </span>
                        <h2 className="text-4xl md:text-5xl font-black mb-6 text-primary tracking-tight">
                            Meet the <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">Builders</span>
                        </h2>
                        <p className="text-secondary text-lg text-center mb-16 max-w-2xl mx-auto leading-relaxed">
                            The engineers, designers, and visionaries dedicating their lives to modernizing the way business is done.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
                        {[
                            { name: "Alex Chen", role: "VPE & Co-Founder", color: "from-blue-500 to-cyan-400" },
                            { name: "Sarah Jenkins", role: "Head of Product", color: "from-emerald-500 to-teal-400" },
                            { name: "David Kim", role: "Lead Architect", color: "from-purple-500 to-indigo-400" },
                            { name: "Elena Rodriguez", role: "Design Director", color: "from-orange-500 to-pink-500" }
                        ].map((member, i) => (
                            <motion.div 
                                key={member.name}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1, duration: 0.5 }}
                                className="group relative overflow-hidden rounded-3xl bg-base aspect-[4/5] transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
                            >
                                {/* Vivid Abstract Gradient Background for empty states */}
                                <div className={`absolute inset-0 bg-gradient-to-br ${member.color} opacity-80 group-hover:scale-110 transition-transform duration-700`}></div>
                                
                                {/* Shadow Overlay to ensure text readability */}
                                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300 z-10"></div>
                                
                                {/* Ambient Icon */}
                                <div className="absolute inset-0 flex items-center justify-center z-10 opacity-30 group-hover:opacity-20 transition-opacity mix-blend-overlay">
                                    <Users className="w-24 h-24 text-white" />
                                </div>

                                {/* Content block */}
                                <div className="absolute bottom-0 left-0 p-6 z-20 w-full transform translate-y-0 md:translate-y-6 md:group-hover:translate-y-0 transition-transform duration-300">
                                    <h3 className="text-white text-xl font-black mb-1">{member.name}</h3>
                                    <p className="text-gray-300 text-sm font-semibold tracking-wide uppercase mb-2">{member.role}</p>
                                    
                                    <div className="mt-4 pt-4 border-t border-white/20 flex gap-3 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-300">
                                        <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white text-white hover:text-gray-900 transition-colors shadow-sm">
                                            <Globe className="w-4 h-4" />
                                        </div>
                                        <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white text-white hover:text-gray-900 transition-colors shadow-sm">
                                            <Code2 className="w-4 h-4" />
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}